import { useEffect, useState } from 'react';
import { Box, Button, FormField, Modal, SpaceBetween, Table, type TableProps } from '@cloudscape-design/components';
import type { ApiClient } from '@deloitte-us-consulting/gp-sdk';
import { PersonAutosuggest, type PersonPickerEntry } from '../../rbac/PersonAutosuggest';
import type { Team, TeamBody, TeamMember } from './usageApi';
import { byText, useTableSort } from './useTableSort';

interface DraftMember {
  email: string;
  name?: string;
  addedBy?: string;
  addedAt?: string;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

const toDraft = (m: TeamMember): DraftMember => ({ email: m.email, ...(m.name ? { name: m.name } : {}), addedBy: m.addedBy, addedAt: m.addedAt });

/** The request body for PUT /api/usage/team: email and optional name only, never a sub. */
export const toTeamBody = (members: readonly { email: string; name?: string | undefined }[]): TeamBody => ({
  members: members.map((m) => ({ email: m.email, ...(m.name ? { name: m.name } : {}) })),
});

/** People left out of the usage numbers (unless "Include platform team" is on). Edit the list, then save. */
export function PlatformTeamModal({
  team,
  api,
  saving,
  onSave,
  onDismiss,
}: {
  team: Team;
  api: Pick<ApiClient, 'get'>;
  saving: boolean;
  onSave: (body: TeamBody) => void;
  onDismiss: () => void;
}) {
  const [members, setMembers] = useState<DraftMember[]>(() => team.members.map(toDraft));
  const [roster, setRoster] = useState<PersonPickerEntry[]>([]);
  const [draftValue, setDraftValue] = useState('');
  const [draftName, setDraftName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<PersonPickerEntry[]>('/api/rbac/roster');
        if (!cancelled) setRoster(data);
      } catch {
        // The picker still accepts a typed email without suggestions.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  const add = () => {
    const email = draftValue.trim().toLowerCase();
    if (!EMAIL.test(email)) return setError('Enter a valid email address.');
    if (members.some((m) => m.email.toLowerCase() === email)) return setError('That person is already on the platform team.');
    setMembers([...members, { email, ...(draftName ? { name: draftName } : {}) }]);
    setDraftValue('');
    setDraftName(null);
    setError(null);
  };

  const columns: TableProps.ColumnDefinition<DraftMember>[] = [
    { id: 'name', header: 'Name', sortingField: 'name', sortingComparator: byText((m) => m.name ?? ''), cell: (m) => m.name ?? '—' },
    { id: 'email', header: 'Email', sortingField: 'email', sortingComparator: byText((m) => m.email), cell: (m) => m.email },
    { id: 'by', header: 'Added by', sortingField: 'by', sortingComparator: byText((m) => m.addedBy ?? ''), cell: (m) => m.addedBy ?? 'Not saved yet' },
    { id: 'on', header: 'Added on', sortingField: 'on', sortingComparator: byText((m) => m.addedAt ?? ''), cell: (m) => (m.addedAt ? formatDate(m.addedAt) : '—') },
    {
      id: 'remove',
      header: 'Remove',
      cell: (m) => (
        <Button
          variant="icon"
          iconName="close"
          ariaLabel={`Remove ${m.name ?? m.email}`}
          onClick={() => setMembers(members.filter((x) => x.email !== m.email))}
        />
      ),
    },
  ];
  // Unsorted to start, so a person just added shows at the bottom where it was typed.
  const sort = useTableSort(members, columns);

  return (
    <Modal
      visible
      size="large"
      onDismiss={onDismiss}
      header="Platform team"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button variant="primary" loading={saving} onClick={() => onSave(toTeamBody(members))}>
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box color="text-body-secondary">
          People on the platform team are left out of the usage numbers unless "Include platform team" is on.
        </Box>
        <FormField label="Add a person" errorText={error}>
          <SpaceBetween direction="horizontal" size="xs">
            <PersonAutosuggest
              value={draftValue}
              onChange={(v, name) => {
                setDraftValue(v);
                setDraftName(name ?? null);
              }}
              people={roster}
            />
            <Button onClick={add} disabled={!draftValue.trim()}>
              Add
            </Button>
          </SpaceBetween>
        </FormField>
        <Table
          variant="embedded"
          {...sort}
          trackBy="email"
          empty={<Box color="text-body-secondary" textAlign="center">No one is on the platform team.</Box>}
        />
      </SpaceBetween>
    </Modal>
  );
}
